// useHostInstitution.js
import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import {
  GET_ALL_HOST_INSTITUTIONS,
  GET_HOST_INSTITUTION_BY_ID,
  GET_HOST_INSTITUTIONS_BY_IDS,
} from "../../queries/hostInstitutionQueries";

export function useHostInstitution(initialId = null) {
  const [hostInstitutionData, setHostInstitutionData] = useState(null);
  const [multipleHostInstitutions, setMultipleHostInstitutions] = useState([]);

  // Query for single host institution
  const [
    getHostInstitution,
    { loading: singleLoading, error: singleError, refetch },
  ] = useLazyQuery(GET_HOST_INSTITUTION_BY_ID, {
    fetchPolicy: "network-only",
    onCompleted: data => {
      if (data && data.hostInstitutionById) {
        setHostInstitutionData(data.hostInstitutionById);
      }
    },
  });

  // Query for all host institutions
  const [getAllHostInstitutions, { loading: allLoading, error: allError }] =
    useLazyQuery(GET_ALL_HOST_INSTITUTIONS, {
      fetchPolicy: "network-only",
      onCompleted: data => {
        if (data && data.hostInstitutions) {
          setMultipleHostInstitutions(data.hostInstitutions);
        }
      },
    });

  // Query for multiple host institutions by IDs
  const [
    getHostInstitutionsByIds,
    { loading: multipleLoading, error: multipleError },
  ] = useLazyQuery(GET_HOST_INSTITUTIONS_BY_IDS, {
    fetchPolicy: "network-only",
    onCompleted: data => {
      if (data && data.hostInstitutionsByIds) {
        setMultipleHostInstitutions(data.hostInstitutionsByIds);
      }
    },
  });

  // Load initial data if ID is provided
  useEffect(() => {
    if (initialId) {
      getHostInstitution({ variables: { _id: initialId } });
    }
  }, [initialId, getHostInstitution]);

  // Format host institution data
  const formatHostInstitutionData = useCallback(rawData => {
    if (!rawData) return null;
    return {
      id: rawData._id,
      name: rawData.name || "-",
      address: rawData.address
        ? {
            full: `${rawData.address.street_number || ""} ${
              rawData.address.street || ""
            }, ${rawData.address.city || ""}, ${
              rawData.address.country || ""
            }`.trim(),
            street: rawData.address.street || "-",
            streetNumber: rawData.address.street_number || "-",
            city: rawData.address.city || "-",
            cityCode: rawData.address.city_code || "-",
            state: rawData.address.state || "-",
            country: rawData.address.country || "-",
          }
        : null,
      adminContact: rawData.admin_contact
        ? {
            fullName: `${rawData.admin_contact.firstname || ""} ${
              rawData.admin_contact.lastname || ""
            }`.trim(),
            email: rawData.admin_contact.email || "-",
            phone: rawData.admin_contact.phone || "-",
          }
        : null,
      itContact: rawData.IT_contact
        ? {
            fullName: `${rawData.IT_contact.firstname || ""} ${
              rawData.IT_contact.lastname || ""
            }`.trim(),
            email: rawData.IT_contact.email || "-",
            phone: rawData.IT_contact.phone || "-",
          }
        : null,
      itEnvironment: rawData.IT_environment?.PACS
        ? {
            manufacturer: rawData.IT_environment.PACS.manufacturer || "-",
            model: rawData.IT_environment.PACS.model || "-",
            ipAddress: rawData.IT_environment.PACS.IP_address || "-",
          }
        : null,
      schemaVersion: rawData.schema_version,
    };
  }, []);

  // Get single host institution data
  const getSingleHostInstitution = useCallback(
    async id => {
      if (!id) {
        return { raw: null, formatted: null };
      }
      try {
        const { data } = await getHostInstitution({ variables: { _id: id } });
        const newHostInstitutionData = data?.hostInstitutionById;
        return {
          raw: newHostInstitutionData,
          formatted: formatHostInstitutionData(newHostInstitutionData),
        };
      } catch (error) {
        return { raw: null, formatted: null };
      }
    },
    [getHostInstitution, formatHostInstitutionData]
  );

  // Get all host institutions
  const fetchAllHostInstitutions = useCallback(async () => {
    try {
      await getAllHostInstitutions();
      return multipleHostInstitutions.map(institution => ({
        raw: institution,
        formatted: formatHostInstitutionData(institution),
      }));
    } catch (error) {
      return [];
    }
  }, [
    getAllHostInstitutions,
    multipleHostInstitutions,
    formatHostInstitutionData,
  ]);

  // Get multiple host institutions by IDs
  const fetchHostInstitutionsByIds = useCallback(
    async ids => {
      if (!ids || !ids.length) {
        return [];
      }
      try {
        await getHostInstitutionsByIds({ variables: { ids } });
        return multipleHostInstitutions.map(institution => ({
          raw: institution,
          formatted: formatHostInstitutionData(institution),
        }));
      } catch (error) {
        return [];
      }
    },
    [
      getHostInstitutionsByIds,
      multipleHostInstitutions,
      formatHostInstitutionData,
    ]
  );

  return {
    hostInstitutionData,
    multipleHostInstitutions,
    loading: singleLoading || allLoading || multipleLoading,
    error: singleError || allError || multipleError,
    getSingleHostInstitution,
    fetchAllHostInstitutions,
    fetchHostInstitutionsByIds,
    formatHostInstitutionData,
    refetch,
  };
}
